import { css } from 'aphrodite';
import { useEffect } from 'react';
import { useLocation } from 'react-router';
import { CampaignType } from '../../../../../models/AdminModels';
import { ILocationState, ISequenceTransitionRequest, ISequenceTransitionStep } from '../../../../../models';
import { useFullscreenModal } from '../../../../../models/hooks/appStateHooks';
import { THEME } from '../../../../components-v2/Theme/theme';
import { MultiContainerHeader } from '../../../../components/MultiContainerHeader';
import { BlogIcon } from '../../../../components/svgs/icons/BlogIcon';
import { EmailIcon } from '../../../../components/svgs/icons/EmailIcon';
import { HeartAndShareIcon } from '../../../../components/svgs/icons/HeartAndShareIcon';
import { ScheduleSendIcon } from '../../../../components/svgs/icons/ScheduleSendIcon';
import { SuccessCheckIcon } from '../../../../components/svgs/icons/SuccessCheckIcon';
import { baseStyleSheet } from '../../../../styles/styles';
import { styleSheet } from './styles';

function getCampaignTypeIcon(type: CampaignType, color: string) {
	switch (type) {
		case CampaignType.Email:
			return <EmailIcon fillColor={color} />;
		case CampaignType.Social:
			return <HeartAndShareIcon fillColor={color} />;
		case CampaignType.Blog:
			return <BlogIcon fill={color} />;
		default:
			return null;
	}
}

function getCampaignTypeLabel(type: CampaignType, count: number) {
	switch (type) {
		case CampaignType.Email:
			return count === 1 ? 'Email campaign' : 'Email campaigns';
		case CampaignType.Social:
			return count === 1 ? 'Social media post' : 'Social media posts';
		case CampaignType.Blog:
			return count === 1 ? 'Blog post' : 'Blog posts';
		default:
			return count === 1 ? 'campaign' : 'campaigns';
	}
}

function RemainingStepCard({ step }: { step: ISequenceTransitionStep }) {
	return (
		<div className={css(styleSheet.remainingCard)}>
			<div className={css(styleSheet.remainingIcon)}>
				{getCampaignTypeIcon(step.type, THEME.colors.primary.contrastText)}
			</div>
			<span className={css(styleSheet.remainingText)}>
				Next: {step.count} {getCampaignTypeLabel(step.type, step.count)} ready to schedule
			</span>
			<button className={css(baseStyleSheet.ctaButtonReverse, styleSheet.scheduleButton)} onClick={step.onSchedule}>
				Schedule Now
			</button>
		</div>
	);
}

export function SequenceTransitionScreen() {
	const location = useLocation<ILocationState<any, ISequenceTransitionRequest>>();
	const request = location.state?.model;
	const fullscreenModal = useFullscreenModal();

	useEffect(() => {
		if (!request) {
			fullscreenModal.dismissModal();
		}
	}, [request, fullscreenModal]);

	if (!request) {
		return null;
	}

	const { completedType, completedCount, remainingSteps, onClose } = request;

	return (
		<div>
			<MultiContainerHeader
				fullscreenHeader='Content Scheduled'
				onFullscreenRequestClose={ev => {
					ev.preventDefault();
					onClose();
				}}
			/>
			<div className={css(styleSheet.container)}>
				<figure className={css(styleSheet.illustrationContainer)}>
					<ScheduleSendIcon />
					<SuccessCheckIcon className={css(styleSheet.successCheckIcon)} />
				</figure>
				<div className={css(styleSheet.completedBanner)}>
					<div className={css(styleSheet.completedIcon)}>
						{getCampaignTypeIcon(completedType, THEME.colors.primary.contrastText)}
					</div>
					<span className={css(styleSheet.completedText)}>
						{completedCount} {getCampaignTypeLabel(completedType, completedCount)} scheduled successfully
					</span>
				</div>
				{remainingSteps.map(step => (
					<RemainingStepCard key={step.type} step={step} />
				))}
				{remainingSteps.length === 0 ? (
					<div className={css(styleSheet.doneContainer)}>
						<button className={css(baseStyleSheet.ctaButton, styleSheet.doneButton)} onClick={onClose}>
							Done
						</button>
					</div>
				) : null}
			</div>
		</div>
	);
}
